import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

enum ReportType {
  POST = 'POST',
  COMMENT = 'COMMENT',
  USER = 'USER',
}

enum ReportStatus {
  PENDING = 'PENDING',
  REVIEWING = 'REVIEWING',
  RESOLVED = 'RESOLVED',
  REJECTED = 'REJECTED',
}

enum ReportReason {
  SPAM = 'SPAM',
  INAPPROPRIATE = 'INAPPROPRIATE',
  HARASSMENT = 'HARASSMENT',
  ILLEGAL = 'ILLEGAL',
  COPYRIGHT = 'COPYRIGHT',
  FALSE_INFO = 'FALSE_INFO',
  ETC = 'ETC',
}

const reportTypeLabels = {
  POST: '게시물',
  COMMENT: '댓글',
  USER: '사용자',
};

const reportStatusLabels = {
  PENDING: '대기중',
  REVIEWING: '검토중',
  RESOLVED: '처리완료',
  REJECTED: '기각됨',
};

const reportReasonLabels = {
  SPAM: '스팸/광고',
  INAPPROPRIATE: '부적절한 내용',
  HARASSMENT: '괴롭힘/욕설',
  ILLEGAL: '불법 정보',
  COPYRIGHT: '저작권 침해',
  FALSE_INFO: '허위 정보',
  ETC: '기타',
};

const statusColors: Record<ReportStatus, "default" | "warning" | "info" | "success" | "error"> = {
  PENDING: 'warning',
  REVIEWING: 'info',
  RESOLVED: 'success',
  REJECTED: 'default',
};

interface Report {
  id: number;
  type: ReportType;
  status: ReportStatus;
  reason: ReportReason;
  description: string | null;
  createdAt: string;
  reporter: {
    id: number;
    username: string;
    name: string;
  };
  post?: {
    id: number;
    description: string;
    author: {
      id: number;
      username: string;
      name: string;
    };
  };
  comment?: {
    id: number;
    description: string;
    commentBy: {
      id: number;
      username: string;
      name: string;
    };
  };
}

const ReportList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [reports, setReports] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [reasonFilter, setReasonFilter] = useState<string>('');
  const [reporterUsername, setReporterUsername] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<number | null>(null);

  useEffect(() => {
    fetchReports();
  }, [page, rowsPerPage]);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (reasonFilter) params.reason = reasonFilter;
      if (reporterUsername) params.reporterUsername = reporterUsername;

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/reports`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReports(response.data.items);
      setTotal(response.data.meta.total);
    } catch (err: any) {
      setError(err.response?.data?.message || '신고 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchReports();
  };

  const handleDelete = async () => {
    if (!reportToDelete) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/admin/reports/${reportToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDeleteDialogOpen(false);
      setReportToDelete(null);
      fetchReports();
    } catch (err: any) {
      setError(err.response?.data?.message || '신고 삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getReportedContent = (report: Report) => {
    if (report.post) {
      return {
        type: '게시물',
        content: report.post.description,
        author: report.post.author.username,
      };
    }
    if (report.comment) {
      return {
        type: '댓글',
        content: report.comment.description,
        author: report.comment.commentBy.username,
      };
    }
    return {
      type: '사용자',
      content: '-',
      author: '-',
    };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">신고 관리</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>타입</InputLabel>
            <Select
              value={typeFilter}
              label="타입"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              {Object.entries(reportTypeLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={statusFilter}
              label="상태"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              {Object.entries(reportStatusLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>신고 사유</InputLabel>
            <Select
              value={reasonFilter}
              label="신고 사유"
              onChange={(e) => setReasonFilter(e.target.value)}
            >
              <MenuItem value="">전체</MenuItem>
              {Object.entries(reportReasonLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="신고자 (username)"
            value={reporterUsername}
            onChange={(e) => setReporterUsername(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{ width: 200 }}
          />

          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} fullWidth={isMobile}>
              검색
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReports} fullWidth={isMobile}>
              새로고침
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isMobile ? (
        <Stack spacing={1.5}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : reports.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">신고 내역이 없습니다</Typography>
            </Paper>
          ) : (
            reports.map((report) => {
              const reportedContent = getReportedContent(report);
              return (
                <Card key={report.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/reports/${report.id}`)}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{report.id} · 신고자 @{report.reporter.username}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, mt: 0.5,
                        }}>
                          {report.description || reportedContent.content}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          <Chip
                            label={reportTypeLabels[report.type]}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10 }}
                          />
                          <Chip
                            label={reportStatusLabels[report.status]}
                            size="small"
                            color={statusColors[report.status]}
                            sx={{ height: 20, fontSize: 10 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {reportReasonLabels[report.reason]}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          대상: {reportedContent.type} @{reportedContent.author} · {formatDate(report.createdAt)}
                        </Typography>
                      </Box>
                      <Stack direction="column" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setReportToDelete(report.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
      <TableContainer component={Paper}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>타입</TableCell>
                <TableCell>신고 사유</TableCell>
                <TableCell>신고자</TableCell>
                <TableCell>신고 대상</TableCell>
                <TableCell>신고 내용</TableCell>
                <TableCell align="center">상태</TableCell>
                <TableCell>신고일</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report) => {
                const reportedContent = getReportedContent(report);
                return (
                  <TableRow
                    key={report.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/reports/${report.id}`)}
                  >
                    <TableCell>{report.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={reportTypeLabels[report.type]}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{reportReasonLabels[report.reason]}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{report.reporter.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{report.reporter.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {reportedContent.type}
                      </Typography>
                      <Typography variant="caption">
                        @{reportedContent.author}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {report.description || reportedContent.content}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={reportStatusLabels[report.status]}
                        size="small"
                        color={statusColors[report.status]}
                      />
                    </TableCell>
                    <TableCell>{formatDate(report.createdAt)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReportToDelete(report.id);
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="페이지당 개수:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 총 ${count}개`}
        />
      </TableContainer>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>신고 삭제 확인</DialogTitle>
        <DialogContent>
          이 신고를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportList;
